import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { SearchableSelect } from 'components/common/SearchableSelect';
import { selectCustomGroups } from 'store/dataReducers/rbacSlice';
import { FeatureGroupNode as FeatureGroupNodeType } from 'types/apis/rbacInterfaces';

interface CreateFeatureGroupDialogProps {
  open: boolean;
  defaultParentId?: number | null;
  onClose: () => void;
  onCreate: (body: { key: string; name: string; description?: string; parent_group_id?: number | null }) => Promise<void>;
}

export const flattenGroups = (
  nodes: FeatureGroupNodeType[],
  depth = 0,
  acc: Array<{ id: number; name: string; depth: number; parent_group_id?: number | null }> = [],
): Array<{ id: number; name: string; depth: number; parent_group_id?: number | null }> => {
  nodes.forEach((node) => {
    acc.push({ id: node.id, name: node.name, depth, parent_group_id: node.parent_group_id });
    flattenGroups(node.children || [], depth + 1, acc);
  });
  return acc;
};

export const CreateFeatureGroupDialog = ({ open, defaultParentId = null, onClose, onCreate }: CreateFeatureGroupDialogProps) => {
  // Only custom groups are valid parents — a subgroup under a System
  // (blueprint) group would render inside the read-only System tab.
  const customGroups = useSelector(selectCustomGroups);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  const parentOptions = useMemo(
    () => [
      { value: '', label: 'None (top level)' },
      ...flattenGroups(customGroups).map((g) => ({
        value: String(g.id),
        label: `${' '.repeat(g.depth * 3)}${g.name}`,
      })),
    ],
    [customGroups],
  );

  useEffect(() => {
    if (open) setParentId(defaultParentId ?? '');
  }, [open, defaultParentId]);

  const handleClose = () => {
    setKey('');
    setName('');
    setDescription('');
    setParentId('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!key.trim() || !name.trim()) return;
    setSubmitting(true);
    await onCreate({
      key: key.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      parent_group_id: parentId === '' ? null : parentId,
    });
    setSubmitting(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Feature Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <Box>
            <SearchableSelect
              label="Parent group"
              value={parentId === '' ? '' : String(parentId)}
              onChange={(value) => setParentId(value === '' ? '' : Number(value))}
              options={parentOptions}
              fullWidth
              placeholder="Type to search groups…"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Optional — nest this group inside another custom group
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !key.trim() || !name.trim()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
